import "./CompanyWarehouses.css";

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { usePermissions } from "./PermissionContext";

const API_URL = "/api";

const emptyWarehouse = () => ({
  code: "",
  name: "",
  address: "",
  notes: "",
});

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

function CompanyWarehouses() {
  const { can } = usePermissions();

  const canManageWarehouses = can("inventory.manage");

  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState(emptyWarehouse);
  const [editingWarehouse, setEditingWarehouse] =
    useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadWarehouses = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}/warehouses/me`,
        {
          headers: getHeaders(),
        }
      );

      setWarehouses(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  const resetForm = () => {
    setForm(emptyWarehouse());
    setEditingWarehouse(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const startEdit = (warehouse) => {
    setEditingWarehouse(warehouse);

    setForm({
      code: warehouse.code,
      name: warehouse.name,
      address: warehouse.address || "",
      notes: warehouse.notes || "",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const saveWarehouse = async (event) => {
    event.preventDefault();

    if (!canManageWarehouses) {
      toast.error(
        "No tienes permiso para administrar bodegas."
      );
      return;
    }

    if (!form.name.trim()) {
      toast.error("Ingresa el nombre de la bodega.");
      return;
    }

    if (!editingWarehouse && !form.code.trim()) {
      toast.error("Ingresa el código de la bodega.");
      return;
    }

    setSaving(true);

    try {
      if (editingWarehouse) {
        await axios.patch(
          `${API_URL}/warehouses/me/${editingWarehouse.id}`,
          {
            name: form.name.trim(),
            address: form.address.trim() || null,
            notes: form.notes.trim() || null,
          },
          {
            headers: getHeaders(),
          }
        );

        toast.success("Bodega actualizada.");
      } else {
        await axios.post(
          `${API_URL}/warehouses/me`,
          {
            code: form.code.trim().toUpperCase(),
            name: form.name.trim(),
            address: form.address.trim() || null,
            notes: form.notes.trim() || null,
          },
          {
            headers: getHeaders(),
          }
        );

        toast.success("Bodega creada.");
      }

      resetForm();
      await loadWarehouses();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (warehouse) => {
    if (!canManageWarehouses) {
      toast.error(
        "No tienes permiso para administrar bodegas."
      );
      return;
    }

    try {
      await axios.patch(
        `${API_URL}/warehouses/me/${warehouse.id}/status`,
        {
          is_active: !warehouse.is_active,
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        warehouse.is_active
          ? "Bodega inactivada."
          : "Bodega activada."
      );

      await loadWarehouses();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="company-warehouses">
      <section className="warehouses-heading">
        <div>
          <p className="warehouses-kicker">Inventario</p>
          <h1>Bodegas</h1>
          <p>
            Administra los lugares donde se almacena y se mueve
            el inventario de la empresa.
          </p>
        </div>

        <button
          type="button"
          className="btn-warehouse-refresh"
          onClick={loadWarehouses}
          disabled={loading}
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </section>

      {canManageWarehouses ? (
        <section className="warehouse-card warehouse-form-card">
          <div className="warehouse-card-heading">
            <div>
              <h2>
                {editingWarehouse
                  ? "Editar bodega"
                  : "Nueva bodega"}
              </h2>

              <p>
                La Bodega principal contiene inicialmente el
                inventario existente.
              </p>
            </div>

            {editingWarehouse && (
              <button
                type="button"
                className="btn-warehouse-cancel"
                onClick={resetForm}
              >
                Cancelar edición
              </button>
            )}
          </div>

          <form onSubmit={saveWarehouse}>
            <div className="warehouse-form-grid">
              <label>
                Código
                <input
                  type="text"
                  name="code"
                  maxLength="30"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="Ejemplo: NORTE"
                  disabled={Boolean(editingWarehouse)}
                  required={!editingWarehouse}
                />
              </label>

              <label>
                Nombre
                <input
                  type="text"
                  name="name"
                  maxLength="120"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Ejemplo: Bodega Norte"
                  required
                />
              </label>

              <label className="warehouse-field-wide">
                Dirección o ubicación
                <input
                  type="text"
                  name="address"
                  maxLength="255"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Ejemplo: Calle 20 # 10-15"
                />
              </label>
            </div>

            <label className="warehouse-notes">
              Observaciones
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows="2"
                placeholder="Información adicional sobre la bodega"
              />
            </label>

            <button
              type="submit"
              className="btn-warehouse-save"
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : editingWarehouse
                  ? "Guardar cambios"
                  : "Crear bodega"}
            </button>
          </form>
        </section>
      ) : (
        <div className="warehouse-readonly-message">
          No tienes permiso para administrar bodegas.
        </div>
      )}

      <section className="warehouse-card">
        <div className="warehouse-card-heading">
          <div>
            <h2>Bodegas registradas</h2>
            <p>
              Las bodegas inactivas no pueden recibir, vender ni
              transferir inventario.
            </p>
          </div>
        </div>

        <div className="warehouse-table-wrapper">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Bodega</th>
                <th>Ubicación</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="warehouse-empty">
                    Cargando bodegas...
                  </td>
                </tr>
              ) : warehouses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="warehouse-empty">
                    No hay bodegas registradas.
                  </td>
                </tr>
              ) : (
                warehouses.map((warehouse) => (
                  <tr key={warehouse.id}>
                    <td>
                      <strong>{warehouse.code}</strong>
                    </td>

                    <td>
                      <strong>{warehouse.name}</strong>

                      {warehouse.is_default && (
                        <small className="warehouse-default-badge">
                          Bodega principal
                        </small>
                      )}
                    </td>

                    <td>
                      {warehouse.address || "Sin ubicación"}
                    </td>

                    <td>
                      <span
                        className={
                          warehouse.is_active
                            ? "warehouse-status active"
                            : "warehouse-status inactive"
                        }
                      >
                        {warehouse.is_active
                          ? "Activa"
                          : "Inactiva"}
                      </span>
                    </td>

                    <td className="warehouse-actions">
                      {canManageWarehouses && (
                        <>
                          <button
                            type="button"
                            className="btn-warehouse-edit"
                            onClick={() =>
                              startEdit(warehouse)
                            }
                          >
                            Editar
                          </button>

                          {!warehouse.is_default && (
                            <button
                              type="button"
                              className={
                                warehouse.is_active
                                  ? "btn-warehouse-disable"
                                  : "btn-warehouse-enable"
                              }
                              onClick={() =>
                                updateStatus(warehouse)
                              }
                            >
                              {warehouse.is_active
                                ? "Inactivar"
                                : "Activar"}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default CompanyWarehouses;